import { useState, useEffect,useRef } from 'react';
import {
  Table,
  Button,
  Dialog,
  Form,
  Input,
  Switch,
  Popconfirm,
  Tag,
  Space,
  Tree,
  MessagePlugin
} from 'tdesign-react';
import { AddIcon, EditIcon, DeleteIcon, SecuredIcon } from 'tdesign-icons-react';
import { roleApi, menuApi } from '../../api';
import type { Role, Menu } from '../../types';

export default function RoleManage() {
  const [data, setData] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const formRef = useRef<any>(null);

  // 权限分配相关状态
  const [permVisible, setPermVisible] = useState(false);
  const [permRole, setPermRole] = useState<Role | null>(null);
  const [menuTree, setMenuTree] = useState<Menu[]>([]);
  const [selectedMenuIds, setSelectedMenuIds] = useState<(string | number)[]>([]);
  const [permLoading, setPermLoading] = useState(false);

  const columns = [
    { colKey: 'id', title: 'ID', width: 80 },
    { colKey: 'roleName', title: '角色名称', width: 150 },
    { colKey: 'roleCode', title: '角色编码', width: 150 },
    { colKey: 'description', title: '描述', ellipsis: true },
    {
      colKey: 'status',
      title: '状态',
      width: 100,
      cell: ({ row }: any) => (
        <Tag theme={row.status === 1 ? 'success' : 'default'}>
          {row.status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    { colKey: 'sortOrder', title: '排序', width: 80 },
    { colKey: 'createTime', title: '创建时间', width: 180 },
    {
      colKey: 'action',
      title: '操作',
      width: 260,
      cell: ({ row }: any) => (
        <Space>
          <Button
            theme="primary"
            variant="text"
            icon={<EditIcon />}
            onClick={() => handleEdit(row)}
          >
            编辑
          </Button>
          <Button
            theme="warning"
            variant="text"
            icon={<SecuredIcon />}
            onClick={() => handleAssignPermission(row)}
          >
            分配权限
          </Button>
          <Popconfirm
            content="确定要删除该角色吗？"
            onConfirm={() => handleDelete(row.id)}
          >
            <Button theme="danger" variant="text" icon={<DeleteIcon />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const fetchData = async (page = pagination.current, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const res = await roleApi.getList({ pageNum: page, pageSize });
      if (res.code === 200) {
        setData(res.data.list);
        setPagination({
          current: res.data.pageNum,
          pageSize: res.data.pageSize,
          total: res.data.total,
        });
      }
    } catch (err) {
      MessagePlugin.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAdd = () => {
    setEditingRole(null);
    formRef.current?.reset();
    formRef.current?.setFieldsValue({
      status: true,
      sortOrder: 0,
    });
    setDialogVisible(true);
  };

  const handleEdit = (role: Role) => {
    setEditingRole(role);
    formRef.current?.reset();
    formRef.current?.setFieldsValue({
      roleName: role.roleName,
      roleCode: role.roleCode,
      description: role.description,
      status: role.status === 1,
      sortOrder: role.sortOrder,
    });
    setDialogVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await roleApi.delete(id);
      if (res.code === 200) {
        MessagePlugin.success('删除成功');
        fetchData();
      } else {
        MessagePlugin.error(res.message || '删除失败');
      }
    } catch (err) {
      MessagePlugin.error('删除失败');
    }
  };

  // ===================== 权限分配 =====================
  const buildTreeData = (menus: Menu[]): any[] => {
    return menus.map((menu) => ({
      label: menu.menuName,
      value: menu.id,
      children: menu.children?.length ? buildTreeData(menu.children) : undefined,
    }));
  };

  const handleAssignPermission = async (role: Role) => {
    setPermRole(role);
    setPermLoading(true);
    setSelectedMenuIds([]);
    try {
      const [menuRes, roleRes] = await Promise.all([
        menuApi.getEnabledTree(),
        roleApi.getById(role.id),
      ]);
      if (menuRes.code === 200) {
        setMenuTree(menuRes.data || []);
      }
      if (roleRes.code === 200) {
        setSelectedMenuIds(roleRes.data?.menuIds || []);
      }
      setPermVisible(true);
    } catch {
      MessagePlugin.error('加载权限数据失败');
    } finally {
      setPermLoading(false);
    }
  };

  const handleSavePermission = async () => {
    if (!permRole) return;
    try {
      const res = await roleApi.update(permRole.id, {
        menuIds: selectedMenuIds.map(Number),
      });
      if (res.code === 200) {
        MessagePlugin.success('权限分配成功');
        setPermVisible(false);
        setPermRole(null);
        setSelectedMenuIds([]);
      } else {
        MessagePlugin.error(res.message || '权限分配失败');
      }
    } catch {
      MessagePlugin.error('权限分配失败');
    }
  };

  // ===================== 核心：完全手动控制校验和提交 =====================
  const handleSubmit = async () => {
    // 1. 显式校验
    const validateResult = await formRef.current?.validate();
    console.log('校验结果:', validateResult);
    
    // 检查校验是否通过
    if (validateResult !== true && Object.keys(validateResult).length > 0) {
      MessagePlugin.warning('请完善表单信息');
      return; // 不关闭弹窗
    }
    
    // 2. 获取值
    const values = formRef.current?.getFieldsValue(true);
    
    // 3. 转换数据
    const submitData = {
      ...values,
      status: values.status ? 1 : 0,
    };

    // 4. 提交
    try {
      let res;
      if (editingRole) {
        res = await roleApi.update(editingRole.id, submitData);
      } else {
        res = await roleApi.create(submitData);
      }

      if (res.code === 200) {
        MessagePlugin.success(editingRole ? '更新成功' : '创建成功');
        setDialogVisible(false);
        formRef.current?.reset();
        fetchData();
      } else {
        MessagePlugin.error(res.message || '操作失败');
      }
    } catch (err) {
      MessagePlugin.error('提交失败，请稍后重试');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">角色管理</h1>
        <Button theme="primary" icon={<AddIcon />} onClick={handleAdd}>
          新增角色
        </Button>
      </div>

      <Table
        data={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          onChange: (pageInfo: any) => fetchData(pageInfo.current, pageInfo.pageSize),
        }}
      />

      {/* ===================== 弹窗（完全自定义按钮）===================== */}
      <Dialog
        header={editingRole ? '编辑角色' : '新增角色'}
        visible={dialogVisible}
        onClose={() => {
          setDialogVisible(false);
          formRef.current?.reset();
        }}
        footer={null}  // ✅ 禁用自带按钮
        width={500}
      >
        <Form ref={formRef} initialData={editingRole || {}} labelWidth={100}>
          <Form.FormItem 
            label="角色名称" 
            name="roleName" 
            rules={[{ required: true, message: '请输入角色名称' }]}
          >
            <Input placeholder="请输入角色名称" />
          </Form.FormItem>
          
          <Form.FormItem 
            label="角色编码" 
            name="roleCode" 
            rules={[{ required: true, message: '请输入角色编码' }]}
          >
            <Input placeholder="请输入角色编码" disabled={!!editingRole} />
          </Form.FormItem>
          
          <Form.FormItem label="描述" name="description">
            <Input placeholder="请输入角色描述" />
          </Form.FormItem>
          
          <Form.FormItem label="状态" name="status" initialValue={true}>
            <Switch />
          </Form.FormItem>
          
          <Form.FormItem label="排序" name="sortOrder" initialValue={0}>
            <Input type="number" placeholder="请输入排序" />
          </Form.FormItem>
        </Form>

        {/* ✅ 完全自定义底部按钮 */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <Button 
            theme="default" 
            variant="outline"
            onClick={() => {
              setDialogVisible(false);
              form.reset();
            }}
          >
            取消
          </Button>
          <Button theme="primary" onClick={handleSubmit}>
            {editingRole ? '保存修改' : '确认创建'}
          </Button>
        </div>
      </Dialog>

      {/* ===================== 权限分配弹窗 ===================== */}
      <Dialog
        header={permRole ? `分配权限 - ${permRole.roleName}` : '分配权限'}
        visible={permVisible}
        onClose={() => {
          setPermVisible(false);
          setPermRole(null);
          setSelectedMenuIds([]);
        }}
        footer={null}
        width={500}
      >
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          <Tree
            data={buildTreeData(menuTree)}
            checkable
            value={selectedMenuIds}
            valueMode="all"
            onChange={(value) => setSelectedMenuIds(value)}
          />
        </div>
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <Button
            theme="default"
            variant="outline"
            onClick={() => {
              setPermVisible(false);
              setPermRole(null);
              setSelectedMenuIds([]);
            }}
          >
            取消
          </Button>
          <Button theme="primary" onClick={handleSavePermission} loading={permLoading}>
            保存权限
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
